/**
 * ContractsPage — super_admin only.
 * List, create and edit contracts that drive org subscriptions.
 *
 * Fas Q1 (P1 platform-admin completion):
 *   - Planer hämtas dynamiskt från /admin/plans (ersätter hårdkodade PACKAGES)
 *   - ?customer_id=X i URL: pre-select org + banner "Skapar avtal för: ..."
 *   - Plan-preview infobox visar limits, OCN/AI, pris vid val
 *   - Auto-fill limits + pris från vald plan (create-läge, kan överstyras)
 *   - "+ Skapa ny kundorganisation..." sista val i org-dropdown öppnar modal
 *   - Route /admin/contracts/new (alias) öppnar formuläret direkt
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, X, FileText, AlertTriangle, Info, Check } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUSES = [
  { value: 'utkast',    label: 'Utkast' },
  { value: 'aktivt',    label: 'Aktivt' },
  { value: 'framtida',  label: 'Framtida' },
  { value: 'utgånget',  label: 'Utgånget' },
  { value: 'avslutat',  label: 'Avslutat' },
];

const BILLING = [
  { value: 'monthly', label: 'Månadsvis' },
  { value: 'annual',  label: 'Årsvis' },
];

const STATUS_COLORS = {
  utkast:    'bg-gray-100 text-gray-700',
  aktivt:    'bg-green-100 text-green-700',
  framtida:  'bg-blue-100 text-blue-700',
  'utgånget': 'bg-yellow-100 text-yellow-700',
  avslutat:  'bg-red-100 text-red-700',
};

const NEW_CUSTOMER_SENTINEL = '__create_new_customer__';

const selectCls = (err) =>
  `w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

// ── Form state builders ──────────────────────────────────────────────────────

const EMPTY = {
  org_id: '', package_code: 'start', status: 'utkast',
  start_date: '', end_date: '', billing_period: 'monthly',
  monthly_price: '', implementation_fee: '', ocn_enabled: false,
  ocn_source: '', max_users: '', max_active_cases_per_year: '',
  max_verksamheter: '', notes: '',
};

const fromContract = (c) => ({
  org_id:                    c.org_id,
  package_code:              c.package_code,
  status:                    c.status,
  start_date:                c.start_date || '',
  end_date:                  c.end_date || '',
  billing_period:            c.billing_period || 'monthly',
  monthly_price:             c.monthly_price != null ? String(c.monthly_price) : '',
  implementation_fee:        c.implementation_fee != null ? String(c.implementation_fee) : '',
  ocn_enabled:               c.ocn_enabled || false,
  ocn_source:                c.ocn_source || '',
  max_users:                 c.max_users != null ? String(c.max_users) : '',
  max_active_cases_per_year: c.max_active_cases_per_year != null ? String(c.max_active_cases_per_year) : '',
  max_verksamheter:          c.max_verksamheter != null ? String(c.max_verksamheter) : '',
  notes:                     c.notes || '',
});

// ── NewCustomerModal ─────────────────────────────────────────────────────────

const NewCustomerModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', org_number: '', contact_person: '', email: '', phone: '',
  });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await api.post('/admin/customers', form);
      toast.success('Kundorganisation skapad');
      onCreated(resp.data);
      onClose();
      setForm({ name: '', org_number: '', contact_person: '', email: '', phone: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa kund');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
      data-testid="contract-new-customer-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Ny kundorganisation</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Namn *</Label>
            <Input required value={form.name} onChange={setField('name')} data-testid="new-customer-name" />
          </div>
          <div>
            <Label>Org.nummer * (XXXXXX-XXXX)</Label>
            <Input
              required value={form.org_number} onChange={setField('org_number')}
              pattern="\d{6}-\d{4}" placeholder="556123-4567"
              data-testid="new-customer-orgnr"
              className="font-mono"
            />
          </div>
          <div>
            <Label>Kontaktperson *</Label>
            <Input required value={form.contact_person} onChange={setField('contact_person')} />
          </div>
          <div>
            <Label>E-post *</Label>
            <Input required type="email" value={form.email} onChange={setField('email')} />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input value={form.phone} onChange={setField('phone')} />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="ghost" className="rounded-full" onClick={onClose}>Avbryt</Button>
            <Button
              type="submit"
              data-testid="new-customer-submit"
              disabled={submitting}
              className="rounded-full font-bold"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {submitting ? 'Sparar...' : 'Skapa kund'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── ContractForm ─────────────────────────────────────────────────────────────

const ContractForm = ({
  initial, orgs, plans, onSave, onCancel, isEdit, contractId,
  prefilledCustomerId, onOpenNewCustomer,
}) => {
  const [form, setForm]      = useState(initial);
  const [errors, setErrors]  = useState({});
  const [submitting, setSub] = useState(false);

  const setField = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const activePlans = useMemo(
    () => (plans || []).filter(p => p.is_active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [plans],
  );

  const selectedPlan = useMemo(
    () => (plans || []).find(p => p.code === form.package_code) || null,
    [plans, form.package_code],
  );

  const prefilledCustomer = useMemo(
    () => (prefilledCustomerId ? orgs.find(o => o.id === prefilledCustomerId) : null),
    [orgs, prefilledCustomerId],
  );

  const prefilledCustomerHasContract = !!prefilledCustomer?.has_active_contract;

  const handlePlanChange = (newCode) => {
    setErrors(e => ({ ...e, package_code: '' }));
    setForm(f => {
      const plan = (plans || []).find(p => p.code === newCode);
      if (!plan || isEdit) {
        return { ...f, package_code: newCode };
      }
      // Auto-fill från plan i create-läge
      return {
        ...f,
        package_code: newCode,
        max_users: plan.max_users != null ? String(plan.max_users) : '',
        max_active_cases_per_year: plan.max_cases_per_year != null ? String(plan.max_cases_per_year) : '',
        max_verksamheter: plan.max_verksamheter != null ? String(plan.max_verksamheter) : '',
        monthly_price: plan.monthly_price_sek != null ? String(plan.monthly_price_sek) : '',
        ocn_enabled: !!plan.ocn_enabled,
      };
    });
  };

  const handleOrgChange = (value) => {
    if (value === NEW_CUSTOMER_SENTINEL) {
      onOpenNewCustomer();
      return;
    }
    setField('org_id', value);
  };

  const validate = (f) => {
    const e = {};
    if (!f.org_id)     e.org_id     = 'Organisation är obligatorisk';
    if (!f.start_date) e.start_date = 'Startdatum är obligatoriskt';
    if (!f.end_date)   e.end_date   = 'Slutdatum är obligatoriskt';
    if (!f.max_users || Number(f.max_users) < 1)
      e.max_users = 'Max användare krävs';
    if (!f.max_active_cases_per_year || Number(f.max_active_cases_per_year) < 1)
      e.max_active_cases_per_year = 'Max case/år krävs';
    if (!f.max_verksamheter || Number(f.max_verksamheter) < 1)
      e.max_verksamheter = 'Max verksamheter krävs';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;

    setSub(true);
    try {
      const payload = {
        org_id:                    form.org_id,
        package_code:              form.package_code,
        status:                    form.status,
        start_date:                form.start_date,
        end_date:                  form.end_date,
        billing_period:            form.billing_period,
        monthly_price:             Number(form.monthly_price) || 0,
        implementation_fee:        form.implementation_fee ? Number(form.implementation_fee) : null,
        ocn_enabled:               form.ocn_enabled,
        ocn_source:                form.ocn_enabled ? form.ocn_source || null : null,
        max_users:                 Number(form.max_users),
        max_active_cases_per_year: Number(form.max_active_cases_per_year),
        max_verksamheter:          Number(form.max_verksamheter),
        notes:                     form.notes.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/admin/contracts/${contractId}`, payload);
        toast.success('Avtal uppdaterat!');
      } else {
        await api.post('/admin/contracts', payload);
        toast.success('Avtal skapat!');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte spara avtal');
    } finally {
      setSub(false);
    }
  };

  return (
    <Card
      className="border-2 animate-in fade-in-0 slide-in-from-top-2 duration-200"
      style={{ borderColor: '#F9CF3F' }}
      data-testid="contract-inline-form"
    >
      <CardContent className="pt-5 pb-6 px-6">
        {/* Banner: prefilled customer */}
        {prefilledCustomer && !isEdit && (
          <div
            className="mb-5 p-3 rounded text-sm flex items-start gap-2"
            style={{ background: '#fffbeb', borderLeft: '4px solid #F9CF3F' }}
            data-testid="prefilled-customer-banner"
          >
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <p>Skapar avtal för: <strong>{prefilledCustomer.name}</strong></p>
              {prefilledCustomerHasContract && (
                <p className="mt-1 text-orange-700 flex items-center gap-1">
                  <AlertTriangle size={14} />
                  Denna kund har redan ett aktivt avtal. Du kan fortsätta — gamla avtalet förblir oförändrat.
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate data-testid="contract-form">

          {/* ══ Organisation ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Organisation
          </p>
          <div className="space-y-1.5 mb-6">
            <Label htmlFor="c-org">
              Organisation <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <select
              id="c-org"
              data-testid="contract-org-select"
              value={form.org_id}
              onChange={e => handleOrgChange(e.target.value)}
              className={selectCls(errors.org_id)}
              disabled={isEdit}
              aria-required="true"
              aria-invalid={!!errors.org_id}
              autoFocus
            >
              <option value="">Välj organisation...</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              {!isEdit && (
                <option value={NEW_CUSTOMER_SENTINEL}>+ Skapa ny kundorganisation...</option>
              )}
            </select>
            {errors.org_id && <p role="alert" className="text-xs text-destructive">{errors.org_id}</p>}
          </div>

          {/* ══ Paket & Status ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Avtalsinformation
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-package">Paket</Label>
              <select
                id="c-package"
                data-testid="contract-package-select"
                value={form.package_code}
                onChange={e => handlePlanChange(e.target.value)}
                className={selectCls(false)}
              >
                {activePlans.length === 0 ? (
                  <option value={form.package_code}>{form.package_code}</option>
                ) : (
                  activePlans.map(p => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))
                )}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-status">Status</Label>
              <select
                id="c-status"
                data-testid="contract-status-select"
                value={form.status}
                onChange={e => setField('status', e.target.value)}
                className={selectCls(false)}
              >
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Plan-preview */}
          {selectedPlan && (
            <div
              className="mb-6 p-3 rounded text-sm"
              style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}
              data-testid="plan-preview-box"
            >
              <p className="font-semibold mb-2">{selectedPlan.name} — översikt</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div>Max användare: <strong>{selectedPlan.max_users ?? 'Obegränsat'}</strong></div>
                <div>Max case/år: <strong>{selectedPlan.max_cases_per_year ?? 'Obegränsat'}</strong></div>
                <div>Max verksamheter: <strong>{selectedPlan.max_verksamheter ?? 'Obegränsat'}</strong></div>
                <div className="flex items-center gap-1">
                  OCN: {selectedPlan.ocn_enabled ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-gray-400" />}
                </div>
                <div className="flex items-center gap-1">
                  AI: {selectedPlan.ai_enabled ? <Check size={12} className="text-green-700" /> : <X size={12} className="text-gray-400" />}
                </div>
                <div>Pris: <strong>{(selectedPlan.monthly_price_sek || 0).toLocaleString('sv-SE')} kr/mån</strong></div>
              </div>
              {!isEdit && (
                <>
                  <p className="text-xs text-gray-600 mt-2">
                    Standardlimits från <strong>{selectedPlan.name}</strong>. Du kan justera per kund.
                  </p>
                  <p className="text-xs text-gray-600">
                    Standardpris från <strong>{selectedPlan.name}</strong>. Du kan justera för rabatter.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Datum */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-start">
                Startdatum <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="c-start"
                type="date"
                data-testid="contract-start-date"
                value={form.start_date}
                onChange={e => setField('start_date', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.start_date}
                className={errors.start_date ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.start_date && <p role="alert" className="text-xs text-destructive">{errors.start_date}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-end">
                Slutdatum <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="c-end"
                type="date"
                data-testid="contract-end-date"
                value={form.end_date}
                onChange={e => setField('end_date', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.end_date}
                className={errors.end_date ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.end_date && <p role="alert" className="text-xs text-destructive">{errors.end_date}</p>}
            </div>
          </div>

          {/* Fakturering & Pris */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="c-billing">Faktureringsperiod</Label>
              <select
                id="c-billing"
                data-testid="contract-billing-select"
                value={form.billing_period}
                onChange={e => setField('billing_period', e.target.value)}
                className={selectCls(false)}
              >
                {BILLING.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-price">Månadspris (SEK)</Label>
              <Input
                id="c-price"
                type="number"
                data-testid="contract-price"
                value={form.monthly_price}
                onChange={e => setField('monthly_price', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-impl-fee">Implementationsavgift (SEK, valfri)</Label>
              <Input
                id="c-impl-fee"
                type="number"
                data-testid="contract-impl-fee"
                value={form.implementation_fee}
                onChange={e => setField('implementation_fee', e.target.value)}
                placeholder="Valfritt"
              />
            </div>
          </div>

          {/* ══ Limits ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Limits
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="space-y-1.5">
              <Label htmlFor="c-max-users">
                Max användare <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="c-max-users"
                type="number"
                data-testid="contract-max-users"
                value={form.max_users}
                onChange={e => setField('max_users', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.max_users}
                className={errors.max_users ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.max_users && <p role="alert" className="text-xs text-destructive">{errors.max_users}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-max-cases">
                Max case/år <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="c-max-cases"
                type="number"
                data-testid="contract-max-cases"
                value={form.max_active_cases_per_year}
                onChange={e => setField('max_active_cases_per_year', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.max_active_cases_per_year}
                className={errors.max_active_cases_per_year ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.max_active_cases_per_year && (
                <p role="alert" className="text-xs text-destructive">{errors.max_active_cases_per_year}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-max-verk">
                Max verksamheter <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="c-max-verk"
                type="number"
                data-testid="contract-max-verksamheter"
                value={form.max_verksamheter}
                onChange={e => setField('max_verksamheter', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.max_verksamheter}
                className={errors.max_verksamheter ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.max_verksamheter && (
                <p role="alert" className="text-xs text-destructive">{errors.max_verksamheter}</p>
              )}
            </div>
          </div>

          {/* ══ OCN ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            OCN
          </p>
          <div className="space-y-3 mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="c-ocn"
                data-testid="contract-ocn-toggle"
                checked={form.ocn_enabled}
                onChange={e => setField('ocn_enabled', e.target.checked)}
                className="w-4 h-4 rounded border-input accent-[#F9CF3F]"
              />
              <span className="text-sm font-medium">OCN aktiverad</span>
            </label>
            {form.ocn_enabled && (
              <div className="space-y-1.5 ml-7">
                <Label htmlFor="c-ocn-source">OCN-källa</Label>
                <select
                  id="c-ocn-source"
                  data-testid="contract-ocn-source"
                  value={form.ocn_source}
                  onChange={e => setField('ocn_source', e.target.value)}
                  className={selectCls(false)}
                  style={{ maxWidth: 300 }}
                >
                  <option value="">Välj källa...</option>
                  <option value="paket">Ingår i paket</option>
                  <option value="tillägg">Aktiverat som tillägg</option>
                </select>
              </div>
            )}
          </div>

          {/* ══ Noteringar ══ */}
          <div className="space-y-1.5 mb-6">
            <Label htmlFor="c-notes">Noteringar</Label>
            <Textarea
              id="c-notes"
              data-testid="contract-notes"
              placeholder="Valfria noteringar..."
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              rows={3}
            />
          </div>

          {/* ══ Knappar ══ */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-full"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              data-testid="contract-submit-btn"
              disabled={submitting}
              className="rounded-full font-bold px-8"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {submitting ? 'Sparar...' : isEdit ? 'Uppdatera avtal' : 'Skapa avtal'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── ContractsPage ─────────────────────────────────────────────────────────────

const ContractsPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customer_id');
  const isNewPath = location.pathname.endsWith('/new');

  const [contracts, setContracts]       = useState([]);
  const [orgs, setOrgs]                 = useState([]);
  const [plans, setPlans]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterOrg, setFilterOrg]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formOpen, setFormOpen]         = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [prefilledCustomerId, setPrefilledCustomerId] = useState(urlCustomerId || '');

  const fetchData = useCallback(async () => {
    try {
      const [contractsRes, orgsRes, plansRes] = await Promise.all([
        api.get('/admin/contracts'),
        api.get('/admin/customers').catch(() => api.get('/customers')),
        api.get('/admin/plans').catch(() => ({ data: [] })),
      ]);
      setContracts(contractsRes.data || []);
      setOrgs(orgsRes.data || []);
      setPlans(plansRes.data || []);
    } catch {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-öppna formulär om URL har ?customer_id= eller path slutar på /new
  useEffect(() => {
    if (loading) return;
    if ((urlCustomerId || isNewPath) && !formOpen && !editContract) {
      setEditContract(null);
      setFormOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, urlCustomerId, isNewPath]);

  const filtered = contracts.filter(c => {
    if (filterOrg    && c.org_id  !== filterOrg)    return false;
    if (filterStatus && c.status  !== filterStatus)  return false;
    return true;
  });

  const getOrgName = (orgId) => orgs.find(o => o.id === orgId)?.name || orgId;

  const openCreate = () => { setEditContract(null); setFormOpen(true); };
  const openEdit   = (c)  => { setEditContract(c);  setFormOpen(true); };
  const closeForm  = ()   => { setFormOpen(false); setEditContract(null); setPrefilledCustomerId(''); };

  const handleSaved = async () => {
    closeForm();
    await fetchData();
  };

  const handleNewCustomerCreated = async (newCustomer) => {
    await fetchData();
    setPrefilledCustomerId(newCustomer.id);
  };

  // Bygg initial form-state. Om prefilledCustomerId finns, sätt org_id.
  const buildInitial = () => {
    if (editContract) return fromContract(editContract);
    const base = { ...EMPTY };
    if (prefilledCustomerId) base.org_id = prefilledCustomerId;
    // Pre-fill från första aktiva plan om planer är laddade
    const firstPlan = (plans || []).filter(p => p.is_active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))[0];
    if (firstPlan) {
      base.package_code = firstPlan.code;
      base.max_users = firstPlan.max_users != null ? String(firstPlan.max_users) : '';
      base.max_active_cases_per_year = firstPlan.max_cases_per_year != null ? String(firstPlan.max_cases_per_year) : '';
      base.max_verksamheter = firstPlan.max_verksamheter != null ? String(firstPlan.max_verksamheter) : '';
      base.monthly_price = firstPlan.monthly_price_sek != null ? String(firstPlan.monthly_price_sek) : '';
      base.ocn_enabled = !!firstPlan.ocn_enabled;
    }
    return base;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contracts-page">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Avtal</h1>
            <p className="text-muted-foreground mt-1">Hantera avtal som styr prenumerationer</p>
          </div>
          {formOpen ? (
            <Button variant="ghost" className="rounded-full" onClick={closeForm}>
              <X size={16} className="mr-1.5" /> Avbryt
            </Button>
          ) : (
            <Button
              data-testid="create-contract-btn"
              className="rounded-full font-bold"
              style={{ background: '#F9CF3F', color: '#333333' }}
              onClick={openCreate}
            >
              <Plus size={18} className="mr-2" /> Nytt avtal
            </Button>
          )}
        </div>

        {/* ── Inline-formulär ── */}
        {formOpen && (
          <ContractForm
            key={editContract?.id ?? `new-${prefilledCustomerId || 'blank'}-${plans.length}`}
            initial={buildInitial()}
            orgs={orgs}
            plans={plans}
            onSave={handleSaved}
            onCancel={closeForm}
            isEdit={!!editContract}
            contractId={editContract?.id}
            prefilledCustomerId={prefilledCustomerId}
            onOpenNewCustomer={() => setNewCustomerOpen(true)}
          />
        )}

        {/* ── Filter ── */}
        <div className="flex flex-wrap gap-4" data-testid="contracts-filters">
          <select
            data-testid="filter-org"
            value={filterOrg}
            onChange={e => setFilterOrg(e.target.value)}
            className={selectCls(false)}
            style={{ maxWidth: 250 }}
          >
            <option value="">Alla organisationer</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className={selectCls(false)}
            style={{ maxWidth: 200 }}
          >
            <option value="">Alla statusar</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* ── Tabell ── */}
        {loading ? (
          <Card><CardContent className="p-6 text-muted-foreground">Laddar avtal...</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText size={48} className="mx-auto mb-4 text-muted-foreground/20" />
              <p className="text-muted-foreground text-lg">Inga avtal hittades.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm" data-testid="contracts-table">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Organisation</th>
                  <th className="text-left px-4 py-3 font-bold">Paket</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                  <th className="text-left px-4 py-3 font-bold">Period</th>
                  <th className="text-left px-4 py-3 font-bold">Limits</th>
                  <th className="text-left px-4 py-3 font-bold">OCN</th>
                  <th className="text-right px-4 py-3 font-bold" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} data-testid={`contract-row-${c.id}`} className="border-t hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{getOrgName(c.org_id)}</td>
                    <td className="px-4 py-3 capitalize">{c.package_code}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[c.status] || ''}`}>
                        {STATUSES.find(s => s.value === c.status)?.label || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{c.start_date} — {c.end_date}</td>
                    <td className="px-4 py-3 text-xs">
                      {c.max_users} anv / {c.max_active_cases_per_year} case / {c.max_verksamheter} verk
                    </td>
                    <td className="px-4 py-3">{c.ocn_enabled ? 'Ja' : 'Nej'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        data-testid={`edit-contract-${c.id}`}
                        onClick={() => openEdit(c)}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={`Redigera avtal ${c.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Modal: ny kund ── */}
        <NewCustomerModal
          open={newCustomerOpen}
          onClose={() => setNewCustomerOpen(false)}
          onCreated={handleNewCustomerCreated}
        />
      </div>
    </DashboardLayout>
  );
};

export { ContractForm, EMPTY, fromContract };
export default ContractsPage;
